<template>
  <v-container class="pt-12 px-md-7">
    <v-row class="justify-lg-space-between align-center">
      <v-col class="pa-0">
        <v-card-title class="py-0 title font-weight-bold letter-spacing-normal">
          Sightings
        </v-card-title>
      </v-col>
    </v-row>
    <v-row>
      <v-col class="py-3 mb-5">
        <div id="speciesSearch" class="position-relative notranslate">
          <v-autocomplete
            ref="autocomplete"
            v-model="fakeModel"
            :search-input.sync="speciesFilter"
            @input="selectSpecies"
            :items="speciesList"
            :multiple="false"
            attach="#speciesSearch"
            item-text="name"
            return-object
            :filter="() => true"
            label="Search"
            append-icon="$search"
            solo
            class="font-weight-bold"
            color="black"
          />
        </div>
      </v-col>
    </v-row>
    <v-row class="mt-0">
      <v-col class="py-3">
        <v-card class="py-3">
          <v-skeleton-loader
            v-if="!isEditMode && !speciesModel.fetched"
            type="list-item-two-line"
          />
          <v-list v-else dense max-height="400" class="overflow-auto">
            <v-list-item v-if="!attendeeSpecies.length" class="d-flex">
              Nothing to show here!
            </v-list-item>
            <v-list-item
              class="d-flex species-row mt-1"
              v-for="(species, index) in attendeeSpecies"
              :key="index"
            >
              <v-list-item-avatar size="60" class="ma-0" tile>
                <v-img
                  :src="formatImageUrlFromMediaUrl(getMedia(species))"
                  lazy-src="/placeholder.jpg"
                  class="grey lighten-2"
                />
              </v-list-item-avatar>
              <v-list-item-content class="list-content">
                <div class="ml-5 title font-weight-bold letter-spacing-normal">
                  {{ species.name }}
                </div>

                <div
                  class="font-weight-bold letter-spacing-normal align-center ml-5 notranslate"
                >
                  {{
                    request.getGlobalPrice(
                      member.id,
                      species.id,
                      request.tourism_operator_id
                    )
                  }}
                </div>
              </v-list-item-content>
              <v-list-item-action class="notranslate">
                {{ request.getCount(member.id, species.id) }}
              </v-list-item-action>
              <div class="d-flex elevation-3 ml-2">
                <v-list-item-action
                  class="ma-0 d-flex  white black--text text-center justify-center pa-2 v-card--link"
                  @click="reduceSighting(species)"
                >
                  <v-icon
                    title="Reduce Sighting"
                    class="white black--text caption ma-0"
                    size="20"
                  >
                    $minus
                  </v-icon>
                </v-list-item-action>
                <v-divider :vertical="true" />
                <v-list-item-action
                  class="ma-0 d-flex  white black--text text-center justify-center pa-2 v-card--link"
                  @click="increaseSighting(species)"
                >
                  <v-icon
                    title="Increase Sighting"
                    class="white black--text caption ma-0"
                    size="20"
                  >
                    $plus
                  </v-icon>
                </v-list-item-action>
              </div>
            </v-list-item>
          </v-list>
          <v-list v-if="request.getTotalPrice()">
            <v-list-item-action
              class="font-weight-bold letter-spacing-normal ma-0 d-flex  white black--text text-right justify-center pa-2 v-card--link"
            >
              <div>
                {{ 'Total Contribution Amount ' + ' $' }}
                {{ totalAmount }}
              </div>
            </v-list-item-action>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import SpeciesModel from '@models/Species';
import { formatImageUrlFromMediaUrl } from '@/utils';
import { isOnline } from '@helpers';

export default {
  name: 'OfflineTripSightings',
  props: {
    member: {
      type: Object,
      required: true,
    },
    request: {
      type: Object,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    title: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      speciesModel: new SpeciesModel({
        itemsPerPage: 5,
      }),
      fakeModel: {},
      speciesFilter: '',
      frequentSpecies: [],
      speciesList: [],
      species: [],
      disableSpeciesFilter: false,
    };
  },
  computed: {
    attendeeSpecies() {
      let species = [];
      let self = this;
      species = self.frequentSpecies.concat(
        self.species.filter(
          (s) => !self.frequentSpecies.find((sp) => sp.id === s.id)
        )
      );
      return species;
    },
    totalAmount() {
      return this.request.species.reduce((total, sp) => {
        if (this.request.tourism_operator_id) {
          const price = sp.species.prices.find(
            (price) => price.user_id === this.request.tourism_operator_id
          );

          if (!price) {
            return sp.species.cost * sp.count + total;
          }

          return price.amount * sp.count + total;
        }

        return sp.species.cost * sp.count + total;
      }, 0);
    },
  },
  watch: {
    'member.id': {
      handler() {
        this.species = [];
        if (this.speciesFilter) {
          this.speciesFilter = '';
        }
        this.syncSpeciesWithRequest();
      },
      deep: true,
    },
    speciesFilter() {
      if (this.disableSpeciesFilter) {
        this.disableSpeciesFilter = false;
        return;
      }
      if (!this.speciesModel.fetched) {
        return;
      }
      this.fetchSpecies();
    },
  },
  methods: {
    formatImageUrlFromMediaUrl,
    getTitle() {
      let name = this.member.first_name;
      if (name.endsWith('s')) {
        return `${name}' Sightings`;
      }
      return `${name}'s Sightings`;
    },
    async fetchSpecies() {
      this.speciesModel
        .setFilters({
          name: this.speciesFilter,
          excludes: this.attendeeSpecies
            .reduce((ids, species) => ids + ' ' + species.id, '')
            .trim()
            .replace(/\s/g, ', '),
        })
        .setFetchOffline(!isOnline());
      let response = await this.speciesModel.get();
      if (response) {
        this.speciesList = response.data;
      }
    },
    async selectSpecies(species) {
      if (!species) {
        return;
      }
      this.disableSpeciesFilter = true;
      this.fakeModel = {};
      await this.$nextTick();
      if (this.attendeeSpecies.find((s) => s.id === species.id)) {
        $showInfo('The species has already been added!');
        this.fetchSpecies();
        return;
      }
      this.species.push(species);
      await this.$nextTick();
      this.fetchSpecies();
    },
    increaseSighting(species) {
      this.request.changeSightingsData(this.member, species);
    },
    reduceSighting(species) {
      this.request.changeSightingsData(this.member, species, -1);
    },
    async getFrequentSpecies() {
      let model = new SpeciesModel({
        itemsPerPage: this.isEditMode ? 100 : 4,
        filters: {
          tour_id: (!this.isEditMode && this.request.tour_id) || null,
          include_ids: this.request.species
            .reduce((ids, species) => ids + ' ' + species.species_id, '')
            .trim()
            .replace(/\s/g, ', '),
        },
        sortBy: ['frequent'],
        sortDesc: [true],
        fetchOffline: !isOnline(),
      });
      let response = await model.get();
      if (response) {
        this.frequentSpecies = response.data;
      }
      await this.$nextTick();
      await this.fetchSpecies();
    },
  },
  mounted() {
    this.getFrequentSpecies();
  },
};
</script>

<style lang="scss" scoped>
.species-row {
  //background-color: #e6e9f3;
}

.list-content {
  padding: 0 !important;
}
</style>
